import TweenValue from 'lib/tween-value';
import {
    get_scroll_top as get_document_scroll_top,
    function_delay,
    mobile_friendly_resize_callback,
    get_ie_version
} from 'lib/utilities';

const IE_VERSION = get_ie_version();

export default function ScrollAnimations(opts = {}) {
    const options = resolve_options(opts);
    const {speed, maxVel, scrollElement} = options;
    let models = [];
    let scrollTop = 0;
    let defaultTweenValue = 0;
    let defaultTween = TweenValue(scrollTop, speed, maxVel);
    let maxScrollTop = 0;
    let viewportHeight = 0;
    let viewportWidth = 0;
    let viewportTop = 0;
    let loopRunning = false;
    let t1 = Date.now();
    let tDelta = 0;

    const on_mobile_friendly_resize = mobile_friendly_resize_callback(mobile_friendly_resize_handler, true);
    const on_last_resize = function_delay(500, on_resize);

    function constructor() {
        add_events();
        begin_loop();

        on_resize();
        on_last_resize();
    }

    function destroy() {
        remove_events();
        end_loop();
        on_last_resize.clear();

        models = [];
    }

    function resolve_options(opts = {}) {
        return {
            speed: 10,
            maxVel: 4000,
            scrollElement: window,
            ...opts
        }
    }

    function add_events() {
        window.addEventListener('resize', on_mobile_friendly_resize);
        scrollElement.addEventListener('scroll', on_scroll);
    }

    function remove_events() {
        window.removeEventListener('resize', on_mobile_friendly_resize);
        scrollElement.removeEventListener('scroll', on_scroll);
    }

    function mobile_friendly_resize_handler() {
        on_resize();
        // Also trigger delayed resize function to catch any elements resized by a library out of sync with the resize
        // event itself.
        on_last_resize();
    }

    function on_scroll() {
        update_scroll_top();
    }

    function on_resize() {
        update_scroll_top();
        update_viewport_state();
        update_models();
    }

    function force_update() {
        update_animations(true);
    }

    function update_scroll_top() {
        scrollTop = get_scroll_top();
        defaultTween.to(scrollTop);
        update_model_tweens(scrollTop);
    }

    function set_scroll_top(value) {
        scrollTop = value;
        defaultTween.to(value);
        update_model_tweens(value);
    }

    function update_model_tweens(scrollTop) {
        for (const model of models) {
            if (model.tween) {
                model.tween.to(scrollTop);
            }
        }
    }

    function get_scroll_top() {
        if (scrollElement === window) {
            return get_document_scroll_top();
        } else {
            return scrollElement.scrollTop;
        }
    }

    function get_viewport_rect() {
        return (scrollElement === window
            ? document.body
            : scrollElement).getBoundingClientRect();
    }

    function get_viewport_width() {
        return scrollElement === window
            ? window.innerWidth
            : scrollElement.offsetWidth;
    }

    function get_viewport_height() {
        return scrollElement === window
            ? window.innerHeight
            : scrollElement.offsetHeight;
    }

    function get_max_scroll_top() {
        if (scrollElement === window) {
            return scrollTop + get_viewport_rect().bottom - viewportHeight;
        } else {
            return scrollElement.scrollHeight - viewportHeight;
        }
    }

    function update_viewport_state() {
        viewportWidth = get_viewport_width();
        viewportHeight = get_viewport_height();
        maxScrollTop = get_max_scroll_top();
    }

    function call_model_hook(model, hook, ...args) {
        if (model[hook]) {
            return model[hook].call(model, model, ...args);
        }
    }

    function get_viewport_offset() {
        if (scrollElement === window) {
            return 0;
        } else {
            return scrollElement.getBoundingClientRect().top;
        }
    }

    function update_model(model) {
        const rect = model.element.getBoundingClientRect();
        const y = scrollTop + rect.top - viewportTop;
        const width = rect.right - rect.left;
        const height = rect.bottom - rect.top;
        const margin = model.margin ?? viewportHeight / 4;

        const min = 'from' in model
            ? model.from // Allow manual from and to scroll positions
            : Math.max(y - viewportHeight - margin, 0);

        const max = 'to' in model
            ? model.to
            : Math.min(y + height + margin, maxScrollTop);

        if (model.debug) {
            console.log('update_model: min, max, y, rect.top, scrollTop', min, max, y, rect.top, scrollTop);
        }

        return Object.assign(model, {
            y: y,
            min: min,
            max: max,
            range: max - min,
            width: width,
            height: height,
            rect: rect,
            ratio: null // Reset last ratio to force resize after the initial reset above
        });
    }

    function update_models() {
        // Clear applied animations to get correct dimensions and position
        models.forEach(model => call_model_hook(model, 'reset'));

        // Due to the flick-back scrolling in some browsers causing incorrect calculation of animation dimensions
        viewportTop = get_viewport_offset();

        // Update the models
        models.forEach(update_model);

        // Trigger resize hook
        models.forEach(model => call_model_hook(model, 'resize'));

        // Update animations with new dimension and position data
        update_animations(true);
    }

    function apply_default_config(config) {
        return Object.assign({
            element: null,
            update: null,
            reset: null,
            resize: null,
            smooth: true,
            ratio: null,
            tween: null,
            margin: null
        }, config);
    }

    function config_to_model(config) {
        return update_model(
            apply_default_config(config)
        );
    }

    function is_valid_config(config) {
        if (! (config.element instanceof Element)) {
            console.log('[ScrollAnimations]: Missing config property "element"');

            return false;
        }

        if (typeof config.update !== 'function') {
            console.log('[ScrollAnimations]: Config property "update" must be a function');

            return false;
        }

        if (typeof config.reset !== 'function') {
            console.log('[ScrollAnimations]: Config property "reset" must be a function');

            return false;
        }

        if ('enabled' in config && typeof config.enabled !== 'function') {
            console.log('[ScrollAnimations]: Config property "enabled" must be a function which returns a boolean');

            return false;
        }

        return true;
    }

    function add_animation(config) {
        if (is_valid_config(config)) {
            const model = config_to_model(config);

            models.push(model);

            // Call initial resize hook to allow the animation to set margins and other animation properties
            call_model_hook(model, 'resize');

            return model;
        } else {
            console.error('[ScrollAnimations]: Invalid config specified for animation - errors logged above', config);
        }
    }

    function resolve_offset(model) {
        switch (typeof model.offset) {
            case 'number':
                return model.offset;

            case 'function':
                return model.offset(model, viewportWidth, viewportHeight);

            default:
                return 0;
        }
    }

    function resolve_tween_value(model) {
        return model.tween
            ? model.tween.update(tDelta)
            : defaultTweenValue;
    }

    function resolve_scroll_top(model) {
        return model.smooth
            ? resolve_tween_value(model)
            : scrollTop;
    }

    /**
     * @param model
     * @param force - If true, the model will be updated even if out of bounds.
     */
    function update_animation(model, force = false) {
        const offset = resolve_offset(model);
        const scrollTop = resolve_scroll_top(model) + offset;
        const ratio = (scrollTop - model.min) / (model.max - model.min);
        const bounded = Math.min(ratio > 0 ? ratio : 0, 1);

        // Only call the animation function if the animation element is in range, and if the value differs from the
        // previous value
        if (force || model.ratio !== bounded) {
            model.ratio = bounded;
            model.update(model, bounded, scrollTop);

            // Force update seems to reduce animation lag in IE <= 11
            ! IE_VERSION || IE_VERSION > 11 || model.element.getBoundingClientRect();
        }
    }

    function is_animation_enabled(model) {
        if (typeof model.enabled === 'boolean') {
            return model.enabled;
        }

        if (typeof model.enabled === 'function') {
            return model.enabled(model);
        }

        return true;
    }

    /**
     * @param force - If true, animations out-of-bounds will be updated, too.
     */
    function update_animations(force = false) {
        const t2 = Date.now();
        tDelta = t2 - t1;
        t1 = t2;

        defaultTweenValue = defaultTween.update(tDelta);

        for (const model of models) {
            if (is_animation_enabled(model)) {
                update_animation(model, force);
            }
        }
    }

    function loop_iteration() {
        if (loopRunning) {
            update_animations();

            requestAnimationFrame(loop_iteration);
        }
    }

    function begin_loop() {
        loopRunning = true;
        t1 = Date.now();

        requestAnimationFrame(loop_iteration);
    }

    function end_loop() {
        loopRunning = false;
    }

    constructor();

    return {
        destroy,
        resize: on_resize,
        update: force_update,
        add_animation,
        set_scroll_top
    }
}